// The payment step: the policy's stored cards as radio rows, with the
// add-a-card form beneath. Adding only ADDS (and selects) — the screen's
// Continue is the one act that confirms a card for the quote and moves
// forward, so a half-abandoned add can never silently complete the step.
// Nothing is charged until the finalize milestone; validation catches
// typos (checksum, past expiry, CVC shape) and the carrier's acceptance
// policy when the carrier collects (multi-pay). Chargeability is checked
// too, but in the background: the server mints and verifies each card as
// it is added, and Continue waits on that verdict — a card that will
// refuse refuses here, where a different card is one tap away.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Controller, useForm, useFormState, useWatch } from "react-hook-form";
import cardValidator from "card-validator";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";

import Button, { ButtonSpinner } from "components/Button";
import { CONTINUE_BUTTON_CLASS } from "components/FormV2";
import {
  FieldError,
  FieldLabel,
  inputClass,
} from "components/FormV2/fields/chrome";
import { NetworkError } from "hooks/usePublicAPI";

import { useQuoteFlowAnalytics } from "./analytics";
import {
  addPaymentMethod,
  confirmPaymentMethod,
  deletePaymentMethod,
  fetchPaymentMethods,
  verifyPaymentMethod,
  type FamilyDecline,
  type PaymentMethodsView,
  type PaymentMethodSummary,
} from "./api";
import {
  LINK_BUTTON_CLASS,
  OUTLINE_BUTTON_CLASS,
} from "components/FlowButtons";
import CardBrandIcon, { CARD_BRAND_LABELS } from "./CardBrandIcon";
import DeclineNotice from "./DeclineNotice";
import ScreenChrome from "components/ScreenChrome";
import LoadingScreen from "./LoadingScreen";
import { dollars } from "./payment-copy";

interface CardFormValues {
  cardName: string;
  cardNumber: string;
  expiry: string;
  cvc: string;
  billingZip: string;
}

const EMPTY_CARD: CardFormValues = {
  cardName: "",
  cardNumber: "",
  expiry: "",
  cvc: "",
  billingZip: "",
};

const LOADING_STATUSES = ["Checking what's on file…"] as const;

function digitsOf(value: string): string {
  return value.replace(/\D/g, "");
}

/** Month-granular, so a card expiring this month stays valid through the
 * month's last day in every timezone. */
function expiryError(value: string): string | undefined {
  const match = /^(0[1-9]|1[0-2])\/(\d{2})$/.exec(value);
  if (!match) return "Enter MM/YY";
  const year = 2000 + Number(match[2]);
  const month = Number(match[1]);
  const now = new Date();
  const current = now.getFullYear() * 12 + now.getMonth() + 1;
  if (year * 12 + month < current) return "This card has expired";
  return undefined;
}

/**
 * The value holds exactly what was typed — digits grouped by the brand's
 * own gap positions (Amex 4-6-5), never a trailing separator, so nothing
 * invisible sits after the caret. Autofill and paste arrive ungrouped
 * and come out the same way.
 */
function formatCardNumber(digits: string): string {
  const card = cardValidator.number(digits).card;
  // Brand gaps cover the canonical length; the rare long PANs (Visa and
  // Discover issue 18/19 digits) keep splitting every four past 16 —
  // 4-4-4-4-3, never a seven-digit tail. Amex tops out at 15 and never
  // reaches the extension.
  const gaps = [...(card?.gaps ?? [4, 8, 12])];
  for (let at = 16; at < digits.length; at += 4) {
    if (!gaps.includes(at)) gaps.push(at);
  }
  let out = "";
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && gaps.includes(i)) out += " ";
    out += digits[i];
  }
  return out;
}

function maxNumberLength(digits: string): number {
  const lengths = cardValidator.number(digits).card?.lengths;
  return lengths?.length ? Math.max(...lengths) : 19;
}

/**
 * Digits in, "MM/YY" out — the slash appears only once a third digit
 * exists, so backspacing never fights it. Autofill shapes ("05/2029",
 * "05 / 29") normalize through the same digit strip: anything longer
 * than four digits reads as MM + the year's last two.
 */
function formatExpiry(raw: string): string {
  let digits = raw.replace(/\D/g, "");
  if (digits.length > 4) digits = digits.slice(0, 2) + digits.slice(-2);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
}

/** The verify poll's beat: the mint job's KMS decrypt and four Stripe hops
 * usually finish within a few seconds. */
const VERIFY_POLL_MS = 1000;
/** Past this the copy levels with the family — the card is still being
 * checked — and offers a plain re-check or a different card. */
const VERIFY_TIMEOUT_MS = 45_000;
/** A refusal the server already knows would land in the same frame as
 * the press, which reads as the button having done nothing; the spinner
 * holds at least this long before revealing one. A ready card never
 * waits — the next screen is the answer. */
const DECLINE_REVEAL_FLOOR_MS = 160;

const FALLBACK_DECLINE: FamilyDecline = {
  code: null,
  message:
    "Your card was declined. You can try again, or use a different card.",
  allow_retry: true,
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function findMethod(
  methods: PaymentMethodSummary[],
  policyPaymentId: string,
): PaymentMethodSummary | null {
  return (
    methods.find((method) => method.policy_payment_id === policyPaymentId) ??
    null
  );
}

function paymentBlurb(dueTodayCents: number | null): string {
  if (dueTodayCents === null) {
    return "We won't charge anything until after you sign.";
  }
  if (dueTodayCents === 0) {
    return "Nothing is charged today — this card is for your monthly payments.";
  }
  return `We won't charge anything until after you sign. Then it's ${dollars(dueTodayCents)}.`;
}

interface Decline {
  policyPaymentId: string;
  failure: FamilyDecline;
}

export default function PaymentStep({
  quoteId,
  dueTodayCents,
  onDone,
}: {
  quoteId: string;
  /** The current selection's frozen due-today amount — named in the blurb
   * so the family knows exactly what signing leads to. */
  dueTodayCents: number | null;
  /** Settles once the parent has moved the family on (or decided not to). */
  onDone: () => Promise<void>;
}) {
  const { track, reportFailure } = useQuoteFlowAnalytics();
  const [view, setView] = useState<PaymentMethodsView | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // Continue pressed with saved cards on file but none chosen — the message
  // sits under the list, and the list is brought into view to show it.
  const [selectionError, setSelectionError] = useState<string | null>(null);
  // Counts the presses so a repeat press remounts the alert: the same text
  // set twice is no change to React, and a live region only announces on
  // change.
  const [selectionErrorPresses, setSelectionErrorPresses] = useState(0);
  const methodsRef = useRef<HTMLDivElement | null>(null);
  // Beneath saved cards the add-card form is folded until asked for — by
  // its own header, or by the decline notice offering a different card.
  const [addCardOpen, setAddCardOpen] = useState(false);
  // Bumped when the opened form should also take focus on the name field.
  const [nameFocusRequest, setNameFocusRequest] = useState(0);
  // The card's refusal, revealed only once Continue asked for it — a
  // failed verification never announces itself unprompted.
  const [decline, setDecline] = useState<Decline | null>(null);
  const [verifyTimedOut, setVerifyTimedOut] = useState(false);
  // Set in the effect body, not at construction: StrictMode mounts,
  // unmounts and re-mounts the component in development, and a ref
  // initialized true would stay false after the simulated unmount.
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchPaymentMethods(quoteId)
      .then((loaded) => {
        if (cancelled) return;
        setView(loaded);
        // Exactly one saved card: preselected, Continue-ready. More than
        // one is a real choice; none means the form is the whole screen.
        if (loaded.methods.length === 1) {
          setSelectedId(loaded.methods[0].policy_payment_id);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          reportFailure("fetch_payment_methods", error);
          setLoadFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [quoteId, reportFailure]);

  const blurb = paymentBlurb(dueTodayCents);

  if (loadFailed) {
    return (
      <ScreenChrome
        kicker="Payment method"
        titleA="How should we"
        titleB="charge it?"
        blurb={blurb}
      >
        <p className="mt-8 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-base font-medium text-red-800">
          We couldn't load your saved payment methods — reload the page to try
          again.
        </p>
      </ScreenChrome>
    );
  }
  if (!view) {
    return (
      <LoadingScreen
        kicker="Payment method"
        titleA="How should we"
        titleB="charge it?"
        statuses={LOADING_STATUSES}
      />
    );
  }

  const replaceMethod = (method: PaymentMethodSummary) => {
    setView((current) => {
      if (!current) return current;
      return {
        ...current,
        methods: current.methods.map((entry) =>
          entry.policy_payment_id === method.policy_payment_id ? method : entry,
        ),
      };
    });
  };

  const clearOutcomes = () => {
    setProblem(null);
    setDecline(null);
    setVerifyTimedOut(false);
    setSelectionError(null);
  };

  const onCardAdded = (method: PaymentMethodSummary, duplicate: boolean) => {
    setView((current) => {
      if (!current) return current;
      const already = current.methods.some(
        (entry) => entry.policy_payment_id === method.policy_payment_id,
      );
      return already
        ? current
        : { ...current, methods: [...current.methods, method] };
    });
    setSelectedId(method.policy_payment_id);
    setNote(
      duplicate
        ? "That card's already on file — we've selected it for you."
        : null,
    );
    setAddCardOpen(false);
    clearOutcomes();
  };

  const removeCard = (policyPaymentId: string) => {
    void (async () => {
      setRemovingId(policyPaymentId);
      // A notice about THIS card goes with it; one about another card
      // (a decline the family is still reading) stays.
      setProblem(null);
      if (decline?.policyPaymentId === policyPaymentId) setDecline(null);
      if (selectedId === policyPaymentId) setVerifyTimedOut(false);
      try {
        const fresh = await deletePaymentMethod(quoteId, policyPaymentId);
        setView(fresh);
        setSelectedId((current) =>
          current === policyPaymentId ? null : current,
        );
        track("quote_flow_payment_method_removed");
      } catch (error) {
        reportFailure("remove_payment_method", error);
        setProblem("We couldn't remove that card — give it another moment.");
      } finally {
        setRemovingId(null);
      }
    })();
  };

  /**
   * Continue's whole act: bring the selected card to ready — verifying it
   * when nothing has yet, polling while the mint runs — then confirm it
   * for the quote and move on. A refusal lands in the decline notice
   * right here, where a different card is one tap away; nothing ever
   * advances a screen only to pull the family back.
   */
  const continueWith = (
    policyPaymentId: string,
    options: { forceVerify?: boolean } = {},
  ) => {
    void (async () => {
      setConfirming(true);
      setNote(null);
      clearOutcomes();
      const startedAt = Date.now();
      const holdBeforeReveal = async () => {
        const elapsed = Date.now() - startedAt;
        if (elapsed < DECLINE_REVEAL_FLOOR_MS) {
          await sleep(DECLINE_REVEAL_FLOOR_MS - elapsed);
        }
      };

      // The card vanished under us (removed in another tab, or by the
      // server): say so, rather than inviting a retry that cannot work —
      // expected, not a bug. Anything else genuinely is one. Shared by
      // every request this step can make, tagged with whichever one
      // actually failed.
      const fail = (operation: string, error: unknown) => {
        if (!mountedRef.current) return;
        const cardGone =
          error instanceof NetworkError && error.statusCode === 404;
        if (!cardGone) reportFailure(operation, error);
        setProblem(
          cardGone
            ? "That card is no longer on file — choose another."
            : "We couldn't save that choice — give it another try in a moment.",
        );
      };

      try {
        let method = findMethod(view.methods, policyPaymentId);
        if (!method) return;
        track("quote_flow_payment_method_submitted", {
          instrument_state: method.instrument.state,
          due_today_cents: dueTodayCents,
        });
        const needsVerify =
          options.forceVerify ||
          (method.instrument.state !== "ready" &&
            method.instrument.state !== "pending");
        if (needsVerify) {
          try {
            const verified = await verifyPaymentMethod(
              quoteId,
              policyPaymentId,
            );
            if (!mountedRef.current) return;
            method = verified.method;
            replaceMethod(verified.method);
          } catch (error) {
            fail("verify_payment_method", error);
            return;
          }
        }
        while (method && method.instrument.state === "pending") {
          if (Date.now() - startedAt > VERIFY_TIMEOUT_MS) {
            if (mountedRef.current) {
              track("quote_flow_payment_method_verify_timed_out");
              setVerifyTimedOut(true);
            }
            return;
          }
          await sleep(VERIFY_POLL_MS);
          if (!mountedRef.current) return;
          let fresh: PaymentMethodsView;
          try {
            fresh = await fetchPaymentMethods(quoteId);
          } catch (error) {
            fail("poll_payment_method", error);
            return;
          }
          if (!mountedRef.current) return;
          setView(fresh);
          method = findMethod(fresh.methods, policyPaymentId);
        }
        if (!method) {
          await holdBeforeReveal();
          if (!mountedRef.current) return;
          setProblem("That card is no longer on file — choose another.");
          return;
        }
        if (method.instrument.state === "ready") {
          track("quote_flow_payment_method_verified");
          try {
            await confirmPaymentMethod(quoteId, policyPaymentId);
          } catch (error) {
            fail("confirm_payment_method", error);
            return;
          }
          if (mountedRef.current) {
            track("quote_flow_payment_method_confirmed");
            // Continue stays busy until the parent has swapped the screen;
            // resting for the beat it takes to learn what comes next reads
            // as the press having failed.
            await onDone();
          }
          return;
        }
        await holdBeforeReveal();
        if (!mountedRef.current) return;
        const failure = method.instrument.failure ?? FALLBACK_DECLINE;
        track("quote_flow_payment_method_declined", {
          decline_code: failure.code,
          allow_retry: failure.allow_retry,
        });
        setDecline({ policyPaymentId, failure });
      } catch (error) {
        fail("confirm_payment_method", error);
      } finally {
        if (mountedRef.current) setConfirming(false);
      }
    })();
  };

  // Continue is always live once cards are on file: pressed without a
  // choice it explains itself beside the list instead of sitting greyed
  // out, and scrolls the list into view when it has scrolled away.
  const requestContinue = () => {
    if (removingId !== null) return;
    if (!selectedId) {
      setSelectionError("Please select a payment method.");
      setSelectionErrorPresses((count) => count + 1);
      const list = methodsRef.current;
      if (!list) return;
      const rect = list.getBoundingClientRect();
      const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
      if (inView) return;
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      list.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "start",
      });
      return;
    }
    continueWith(selectedId);
  };

  const useDifferentCard = () => {
    clearOutcomes();
    setSelectedId(null);
    setAddCardOpen(true);
    setNameFocusRequest((count) => count + 1);
  };

  const hasSavedCards = view.methods.length > 0;

  return (
    <ScreenChrome
      kicker="Payment method"
      titleA="How should we"
      titleB="charge it?"
      blurb={blurb}
    >
      <div className="mt-10">
        {hasSavedCards && (
          // scroll-mt leaves headroom above the first card when the list is
          // scrolled into view.
          <div ref={methodsRef} className="flex scroll-mt-5 flex-col gap-3">
            {view.methods.map((method) => (
              <SavedCardRow
                key={method.policy_payment_id}
                method={method}
                selected={selectedId === method.policy_payment_id}
                removing={removingId === method.policy_payment_id}
                onSelect={() => {
                  setSelectedId(method.policy_payment_id);
                  setNote(null);
                  clearOutcomes();
                }}
                onRemove={() => removeCard(method.policy_payment_id)}
              />
            ))}
          </div>
        )}
        {selectionError && (
          <p
            key={selectionErrorPresses}
            role="alert"
            className="mt-4 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-base font-medium text-red-800"
          >
            {selectionError}
          </p>
        )}
        {note && (
          <p className="mt-4 rounded-xl bg-oat px-4 py-3.5 text-base text-soil">
            {note}
          </p>
        )}
        <AddCardForm
          quoteId={quoteId}
          acceptedBrands={view.accepted_credit_cards}
          carrierName={view.carrier_name}
          beneathSavedCards={hasSavedCards}
          open={!hasSavedCards || addCardOpen}
          onToggle={() => setAddCardOpen((current) => !current)}
          nameFocusRequest={nameFocusRequest}
          onAdded={onCardAdded}
        />
        <p className="mt-6 flex items-center gap-1 text-[13px] leading-normal text-oat-700">
          <LockClosedIcon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
          Secured with 256-bit AES encryption
        </p>
        {problem && (
          <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-base font-medium text-red-800">
            {problem}
          </p>
        )}
        {decline && (
          <DeclineNotice
            message={decline.failure.message}
            primary={
              decline.failure.allow_retry
                ? {
                    label: "Try this card again",
                    onClick: () =>
                      continueWith(decline.policyPaymentId, {
                        forceVerify: true,
                      }),
                    busy: confirming,
                  }
                : undefined
            }
            secondary={{
              label: "Use a different card",
              onClick: useDifferentCard,
            }}
          />
        )}
        {verifyTimedOut && selectedId && (
          <DeclineNotice
            message="We're still checking that card — it's taking longer than usual."
            primary={{
              label: "Check again",
              // A verify, not a bare poll: the server restarts a mint whose
              // job died, which is what a long wait usually means.
              onClick: () => continueWith(selectedId, { forceVerify: true }),
              busy: confirming,
            }}
            secondary={{
              label: "Use a different card",
              onClick: useDifferentCard,
            }}
          />
        )}
        {/* Until a card is on file there is nothing to continue with — the
            form's Add card is the only step, so the screen's Continue waits
            for the first saved card. */}
        {hasSavedCards && (
          <div className="mt-8 flex items-center justify-end">
            <Button
              variant="continue"
              isLoading={confirming}
              loadingText="Verifying card…"
              icon={<ChevronRightIcon className="h-4 w-4" aria-hidden="true" />}
              iconPosition="right"
              onClick={requestContinue}
            >
              Continue
            </Button>
          </div>
        )}
      </div>
    </ScreenChrome>
  );
}

function SavedCardRow({
  method,
  selected,
  removing,
  onSelect,
  onRemove,
}: {
  method: PaymentMethodSummary;
  selected: boolean;
  removing: boolean;
  onSelect: () => void;
  onRemove: () => void;
}) {
  // Phones: the radio row takes the full width and Remove wraps beneath the
  // card details, indented past the radio so both rows read as one column
  // beside it. From sm the two share a row with Remove at the far right.
  return (
    <div
      className={`flex flex-wrap items-center gap-x-3.5 gap-y-2 rounded-xl border-2 px-4 py-3.5 transition-colors sm:flex-nowrap ${
        selected ? "border-evergreen bg-white" : "border-oat-200 bg-white"
      }`}
    >
      <button
        type="button"
        className="flex min-w-0 flex-1 basis-full items-center gap-3.5 text-left sm:basis-auto"
        role="radio"
        aria-checked={selected}
        onClick={onSelect}
      >
        <span
          aria-hidden="true"
          className={`flex h-5 w-5 flex-none items-center justify-center rounded-full border-2 ${
            selected ? "border-evergreen" : "border-oat-300"
          }`}
        >
          {selected && (
            <span className="h-2.5 w-2.5 rounded-full bg-evergreen" />
          )}
        </span>
        <CardBrandIcon brand={method.brand} variant="row" />
        <span className="min-w-0">
          {/* Just the digits — the one part that must never truncate on
              a narrow screen; the brand badge says the rest. */}
          <span className="ph-no-capture block text-base font-bold tracking-wide text-soil">
            ···· {method.last_4 ?? "????"}
          </span>
          {method.exp && (
            <span className="ph-no-capture block text-sm text-oat-700">
              Expires {method.exp}
            </span>
          )}
        </span>
      </button>
      {/* The indent lines Remove up with the brand badge: the radio button's
          own 12px side padding (the app's base button style), the 20px
          radio, and the 14px row gap. */}
      <button
        type="button"
        className={`${LINK_BUTTON_CLASS} ml-[46px] sm:ml-0`}
        disabled={removing}
        onClick={onRemove}
      >
        {removing ? "Removing…" : "Remove"}
      </button>
    </div>
  );
}

function AddCardForm({
  quoteId,
  acceptedBrands,
  carrierName,
  beneathSavedCards,
  open,
  onToggle,
  nameFocusRequest,
  onAdded,
}: {
  quoteId: string;
  /** Null = no restriction (single-pay, or an unconfigured carrier). */
  acceptedBrands: string[] | null;
  carrierName: string | null;
  /** True when saved cards sit above the form. Beneath them it wears its
   * own frame and heading and its Add card reads secondary until the
   * family starts typing; as the whole screen it renders bare with Add
   * card as the primary — the one way forward. */
  beneathSavedCards: boolean;
  /** Whether the fields show. Beneath saved cards the form folds to its
   * header and opens on request; as the whole screen it is always open. */
  open: boolean;
  onToggle: () => void;
  /** Bumped by the parent when the opened form should focus the name
   * field — the decline notice's "use a different card". */
  nameFocusRequest: number;
  onAdded: (method: PaymentMethodSummary, duplicate: boolean) => void;
}) {
  const { track, reportFailure } = useQuoteFlowAnalytics();
  const { control, handleSubmit, getValues, reset } = useForm<CardFormValues>({
    defaultValues: EMPTY_CARD,
    mode: "onTouched",
  });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const { isDirty } = useFormState({ control });
  // Add card is the primary whenever it is the obvious next step: with no
  // saved card to pick, or once the family has started typing here. Beneath
  // an untouched saved card it stays secondary so that card stays the
  // obvious choice.
  const addCardIsPrimary = !beneathSavedCards || isDirty;
  // Opening the folded form brings its top to the top of the screen, with
  // the same headroom and motion as the rest of the flow's scrolls.
  const formRef = useRef<HTMLFormElement | null>(null);
  useEffect(() => {
    if (!beneathSavedCards || !open) return;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    formRef.current?.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
  }, [beneathSavedCards, open]);
  useEffect(() => {
    if (nameFocusRequest === 0) return;
    document.getElementById("cardName")?.focus({ preventScroll: true });
  }, [nameFocusRequest]);
  // Validation errors show only at rest: the focused field's error (text
  // and red border) hides while they type and reappears on blur. The
  // refused-brand notice is the exception — it IS the live feedback.
  const [focusedField, setFocusedField] = useState<string | null>(null);
  // Mid-string edits to the card number: the caret lands after the same
  // COUNT OF DIGITS it sat after before the regroup, so inserting or
  // deleting around a space never throws it to the end.
  const numberRef = useRef<HTMLInputElement | null>(null);
  const caretDigits = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = numberRef.current;
    if (!el || caretDigits.current === null) return;
    let pos = 0;
    let seen = 0;
    while (pos < el.value.length && seen < caretDigits.current) {
      if (/\d/.test(el.value[pos])) seen += 1;
      pos += 1;
    }
    el.setSelectionRange(pos, pos);
    caretDigits.current = null;
  });
  const watchedNumber = useWatch({ control, name: "cardNumber" }) ?? "";
  const detected = cardValidator.number(digitsOf(watchedNumber)).card;
  const brand = detected?.type ?? null;
  const cvcSize = detected?.code?.size ?? 3;
  // Refused brands announce themselves as the number is typed — Amex is
  // recognizable from two digits, long before the rest of the form is
  // filled in. Submit-time validation repeats the same message as the
  // backstop.
  const refusedBrandMessage =
    acceptedBrands && brand && !acceptedBrands.includes(brand)
      ? `We're sorry, but ${carrierName ?? "this carrier"} doesn't accept ${CARD_BRAND_LABELS[brand] ?? "that card"}`
      : null;

  const submit = handleSubmit(async (values) => {
    setBusy(true);
    setProblem(null);
    try {
      const added = await addPaymentMethod(quoteId, {
        type: "card",
        cc_full_name: values.cardName.trim(),
        cc_number: digitsOf(values.cardNumber),
        cc_exp: values.expiry,
        cc_cvc: values.cvc,
        cc_billing_address_zip: values.billingZip,
      });
      track("quote_flow_payment_method_added");
      reset(EMPTY_CARD);
      onAdded(added.method, added.duplicate);
    } catch (error) {
      reportFailure("add_payment_method", error);
      setProblem(
        "We couldn't save that card — give it another try in a moment.",
      );
    } finally {
      setBusy(false);
    }
  });

  return (
    <form
      ref={formRef}
      className={
        beneathSavedCards
          ? "mt-6 scroll-mt-5 rounded-xl border-2 border-oat-200 bg-white p-5"
          : ""
      }
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {beneathSavedCards && (
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 bg-transparent p-0 text-left"
          aria-expanded={open}
          aria-controls="add-card-fields"
          onClick={onToggle}
        >
          <h2 className="text-lg font-bold text-soil">Add a new card</h2>
          <ChevronDownIcon
            className={`h-5 w-5 flex-none text-soil transition-transform ${
              open ? "rotate-180" : ""
            }`}
            strokeWidth={2}
            aria-hidden="true"
          />
        </button>
      )}
      {open && (
        <div id="add-card-fields" className={beneathSavedCards ? "mt-5" : ""}>
          <div className="flex flex-col gap-6">
            <Controller
              control={control}
              name="cardName"
              rules={{ required: "This is required" }}
              render={({ field, fieldState: { error } }) => {
                const atRest = focusedField !== "cardName";
                return (
                  <div>
                    <FieldLabel text="Name on card" htmlFor="cardName" />
                    <input
                      id="cardName"
                      className={inputClass(Boolean(error) && atRest)}
                      autoComplete="cc-name"
                      value={field.value}
                      onChange={field.onChange}
                      onFocus={() => setFocusedField("cardName")}
                      onBlur={() => {
                        setFocusedField(null);
                        field.onBlur();
                      }}
                    />
                    <FieldError message={atRest ? error?.message : undefined} />
                  </div>
                );
              }}
            />
            <Controller
              control={control}
              name="cardNumber"
              rules={{
                required: "This is required",
                validate: (value) => {
                  const digits = digitsOf(value);
                  if (digits.length < 13 || digits.length > 19) {
                    return "Enter the full card number";
                  }
                  const info = cardValidator.number(digits);
                  if (!info.isValid) {
                    return "That doesn't look like a valid card number";
                  }
                  const enteredBrand = info.card?.type ?? null;
                  if (
                    acceptedBrands &&
                    enteredBrand &&
                    !acceptedBrands.includes(enteredBrand)
                  ) {
                    return `We're sorry, but ${carrierName ?? "this carrier"} doesn't accept ${CARD_BRAND_LABELS[enteredBrand] ?? "that card"}`;
                  }
                  return true;
                },
              }}
              render={({ field, fieldState: { error } }) => {
                const atRest = focusedField !== "cardNumber";
                // The refused brand outranks everything and never hides —
                // it is the live as-you-type feedback; completeness and
                // checksum wait for blur like every other field.
                const message =
                  refusedBrandMessage ?? (atRest ? error?.message : undefined);
                return (
                  <div>
                    <FieldLabel text="Card number" htmlFor="cardNumber" />
                    <div className="relative">
                      <input
                        id="cardNumber"
                        ref={numberRef}
                        className={`${inputClass(Boolean(message))} pr-[52px]`}
                        inputMode="numeric"
                        autoComplete="cc-number"
                        placeholder="1234 1234 1234 1234"
                        value={field.value}
                        onChange={(event) => {
                          const el = event.target;
                          const caret = el.selectionStart ?? el.value.length;
                          const digitsBefore = el.value
                            .slice(0, caret)
                            .replace(/\D/g, "").length;
                          const digits = digitsOf(el.value);
                          const clamped = digits.slice(
                            0,
                            maxNumberLength(digits),
                          );
                          caretDigits.current = Math.min(
                            digitsBefore,
                            clamped.length,
                          );
                          field.onChange(formatCardNumber(clamped));
                        }}
                        onFocus={() => setFocusedField("cardNumber")}
                        onBlur={() => {
                          setFocusedField(null);
                          field.onBlur();
                        }}
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                        <CardBrandIcon brand={brand} />
                      </span>
                    </div>
                    <FieldError message={message} />
                  </div>
                );
              }}
            />
            {/* Phones: expiry and CVC share a row, the ZIP takes the next; from
            sm all three sit across. */}
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
              <Controller
                control={control}
                name="expiry"
                rules={{
                  required: "This is required",
                  validate: (value) => expiryError(value) ?? true,
                }}
                render={({ field, fieldState: { error } }) => {
                  const atRest = focusedField !== "expiry";
                  return (
                    <div>
                      <FieldLabel text="Expires" htmlFor="expiry" />
                      <input
                        id="expiry"
                        className={inputClass(Boolean(error) && atRest)}
                        inputMode="numeric"
                        autoComplete="cc-exp"
                        placeholder="MM/YY"
                        maxLength={7}
                        value={field.value}
                        onChange={(event) =>
                          field.onChange(formatExpiry(event.target.value))
                        }
                        onFocus={() => setFocusedField("expiry")}
                        onBlur={() => {
                          setFocusedField(null);
                          field.onBlur();
                        }}
                      />
                      <FieldError
                        message={atRest ? error?.message : undefined}
                      />
                    </div>
                  );
                }}
              />
              <Controller
                control={control}
                name="cvc"
                rules={{
                  required: "This is required",
                  validate: (value) => {
                    if (!/^\d{3,4}$/.test(value)) return "3 or 4 digits";
                    // Brand known (from the number already entered): require
                    // its exact CVC length.
                    const known = cardValidator.number(
                      digitsOf(getValues("cardNumber")),
                    ).card;
                    if (known?.code?.size && value.length !== known.code.size) {
                      return `${known.code.name} is ${known.code.size} digits`;
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => {
                  const atRest = focusedField !== "cvc";
                  return (
                    <div>
                      <FieldLabel text="CVC" htmlFor="cvc" />
                      <input
                        id="cvc"
                        className={inputClass(Boolean(error) && atRest)}
                        inputMode="numeric"
                        autoComplete="cc-csc"
                        placeholder={cvcSize === 4 ? "1234" : "123"}
                        value={field.value}
                        onChange={(event) =>
                          field.onChange(
                            event.target.value.replace(/\D/g, "").slice(0, 4),
                          )
                        }
                        onFocus={() => setFocusedField("cvc")}
                        onBlur={() => {
                          setFocusedField(null);
                          field.onBlur();
                        }}
                      />
                      <FieldError
                        message={atRest ? error?.message : undefined}
                      />
                    </div>
                  );
                }}
              />
              <Controller
                control={control}
                name="billingZip"
                rules={{
                  required: "This is required",
                  pattern: { value: /^\d{5}$/, message: "5 digits" },
                }}
                render={({ field, fieldState: { error } }) => {
                  const atRest = focusedField !== "billingZip";
                  return (
                    <div className="col-span-2 sm:col-span-1">
                      <FieldLabel text="Billing ZIP" htmlFor="billingZip" />
                      <input
                        id="billingZip"
                        className={inputClass(Boolean(error) && atRest)}
                        inputMode="numeric"
                        autoComplete="billing postal-code"
                        value={field.value}
                        onChange={(event) =>
                          field.onChange(
                            event.target.value.replace(/\D/g, "").slice(0, 5),
                          )
                        }
                        onFocus={() => setFocusedField("billingZip")}
                        onBlur={() => {
                          setFocusedField(null);
                          field.onBlur();
                        }}
                      />
                      <FieldError
                        message={atRest ? error?.message : undefined}
                      />
                    </div>
                  );
                }}
              />
            </div>
          </div>
          {problem && (
            <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-base font-medium text-red-800">
              {problem}
            </p>
          )}
          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              className={`${
                addCardIsPrimary ? CONTINUE_BUTTON_CLASS : OUTLINE_BUTTON_CLASS
              } w-full sm:w-auto`}
              disabled={busy}
              aria-busy={busy || undefined}
            >
              {busy && <ButtonSpinner />}
              Add card
            </button>
          </div>
        </div>
      )}
    </form>
  );
}
